import { h, Component } from 'preact';
import dlv from 'dlv';
import Provider from  'preact-context-provider';
import { TrackingProvider, trackingProvider, DIMENSION } from '@cube/track';
import cx from 'classnames';
import { Row } from '@cube/layout';
import { Nav, ScrollDirection } from '@cube/blocks';
import { DropDownSearch } from '@cube/search-input';
import LinkEvent, { linkEventLabel, linkEventIcon, linkEventImage } from '../../linkevent';
import { withConfig } from '../../../config';
import style from './style.less';
import { navSkinRounded, topHatNavSkinRounded } from './navskin';
import configFromCurrent from '../../configfromcurrent';
import WidthConstrain from '../../widthconstrain';
import LogoIcon from '../../logoicon';
import EmailPreview from '../../emailpreview';
import { openSearchInNewWindow, createNavigation } from '../../../lib';
import { fetchSpecialTitle } from '../../../lib/scs';
import { WeatherWidget } from '@cube/weather-widget';

function TrackNav({ onMenuOpen, ...props }, context) {
	return (
		<Nav
			{...props}
			onMenuOpen={onMenuOpenWithTracking(onMenuOpen, context)}
		/>
	);
}

function onMenuOpenWithTracking(onMenuOpen, context) {
	if (!onMenuOpen) return;
	return (isOpen, link, meta, isSubMenu) => onMenuOpen(isOpen, link, meta, isSubMenu, context);
}

function TopHatNav ({ links, theme, navType, align, onMenuOpen, openLeft }){
	return (
		<TrackNav
			class={cx('col', style[theme])}
			skin={topHatNavSkinRounded(theme, align)}
			type={navType && (navType.tophat || navType.default|| navType) || 'hover'}
			delaySubnavRender
			items={links}
			onMenuOpen={onMenuOpen}
			openLeft={openLeft}
		/>
	);
}

function TopHat({ theme, leadingLinks, leadingAltLinks, navType, onMenuOpen, showWideView, weatherServiceOrigin, locationServiceOrigin, weatherPlacement }) {
	let hasLinks = leadingLinks && leadingLinks.length > 0;
	return (
		<TrackingProvider {...{
			[DIMENSION.componentpath]: 'TopHat'
		}}
		>
			<Row class={cx(style.topHat, style[theme])}>
				<WidthConstrain class={cx(style.widthConstrain, style[weatherPlacement])} showWideView={showWideView}>
					{
						(weatherPlacement === 'tophatLeft') && (
							<WeatherWidget
								displayMode="mini"
								theme={theme}
								weatherServiceOrigin={weatherServiceOrigin}
								locationServiceOrigin={locationServiceOrigin}
							/>
						)
					}
					{
						(leadingAltLinks && leadingAltLinks.length) && (
							<TrackingProvider {...{
								[DIMENSION.componentpath]: 'LeadingAltLinks'
							}}
							>
								<TopHatNav
									links={leadingAltLinks}
									theme={theme}
									navType={navType}
									onMenuOpen={onMenuOpen}
								/>
							</TrackingProvider>
						)
					}
					{
						(weatherPlacement === 'tophatRight') && (
							<WeatherWidget
								displayMode="mini"
								theme={theme}
								weatherServiceOrigin={weatherServiceOrigin}
								locationServiceOrigin={locationServiceOrigin}
							/>
						)
					}
					{
					//We add the right aligned nav if it is needed:
						hasLinks && (
							<TrackingProvider {...{
								[DIMENSION.componentpath]: 'LeadingLinks'
							}}
							>
								<TopHatNav
									align="alignRight"
									links={leadingLinks}
									theme={theme}
									navType={navType}
									onMenuOpen={onMenuOpen}
									openLeft
								/>
							</TrackingProvider>
						)}

				</WidthConstrain>
			</Row>
		</TrackingProvider>
	);
}

function PrimaryHeader({
	theme,
	hideLogo,
	logo,
	primaryLinks,
	class: cls,
	forceFocus,
	useLargeLogo,
	searchColorTheme,
	showWideView,
	portalStaticOrigin,
	defaultProvider,
	primaryLinkMode,
	emailPreview
}) {
	let context = 'home';

	// Track if we've found the Email link to add the EmailPreview component
	let emailLinkIndex = -1;

	// Find the Email link in primaryLinks - look for both 'Email' label and mail icon
	if (primaryLinks && primaryLinks.length) {
		emailLinkIndex = primaryLinks.findIndex(link =>
			link.label === 'Email'
		);
	}

	const handleEmailHover = (isHovering) => {
		if (typeof window === 'undefined') return;
		const emailEvent = new CustomEvent('emailButtonHover', { detail: { isHovering } });
		document.querySelectorAll('.emailLinkWrapper').forEach(
			element => element.dispatchEvent(emailEvent)
		);
	};

	// Add these specific handler functions
	const handleEmailMouseEnter = () => {
		handleEmailHover(true);
	};

	const handleEmailMouseLeave = () => {
		handleEmailHover(false);
	};

	return (
		<Row class={cx(style.primary, style[theme], cls)}>
			<WidthConstrain class={style.widthConstrain} showWideView={showWideView}>
				{ !hideLogo &&
					<div class={style.logoContainer}>
						<LogoIcon
							theme={theme}
							size="lg"
							link={logo}
							class={cx(style.logo, useLargeLogo && style.large)}
							portalStaticOrigin={portalStaticOrigin}
						/>
					</div>
				}
				<TrackingProvider {...{
					[DIMENSION.text]: searchColorTheme
				}}
				>
					<div class={cx(style.searchContainer, style[theme], style[searchColorTheme])}>
						<DropDownSearch
							class={cx(style.search)}
							context={context}
							buttonClass={cx(style.searchButton)}
							inputTextClass={cx(style.searchInput)}
							forceFocus={forceFocus}
							openSearchInNewWindow={openSearchInNewWindow()}
							defaultProvider={defaultProvider}
						/>
					</div>
				</TrackingProvider>
				<TrackingProvider {...{
					[DIMENSION.componentpath]: 'PrimaryLinks'
				}}
				>
					{ dlv(primaryLinks, '0') &&
						<div class={cx(style.links, style.right)}>
							{
								primaryLinks && primaryLinks.map((link, index) => {
									const isEmailLink = index === emailLinkIndex;
									const isEmailPreviewEnabled = emailPreview && emailPreview.enabled;
									const showEmailPreview = isEmailPreviewEnabled && isEmailLink;
									const shouldUseWrapper = isEmailPreviewEnabled || theme === 'dark';

									// If email preview is not enabled and theme is not dark, render link without wrapper
									if (!shouldUseWrapper) {
										return (
											<LinkEvent
												class={cx(style.userLink, style[theme], style[primaryLinkMode])}
												link={link}
											>
												{linkEventImage(link, { theme, class: style.linkRendererImage })}
												{linkEventIcon(link, { theme, class: style.linkRendererIcon })}
												{linkEventLabel(link, { theme, class: style.linkRendererLabel })}
											</LinkEvent>
										);
									}

									// If email preview is enabled or theme is dark, use the wrapper for all links
									// but only attach special handling to the email link when preview is enabled
									return (
										<div
											class={cx(style.linkWrapper, isEmailLink && isEmailPreviewEnabled && style.emailLinkWrapper)}
											onMouseEnter={showEmailPreview ? handleEmailMouseEnter : null}
											onMouseLeave={showEmailPreview ? handleEmailMouseLeave : null}
										>
											<LinkEvent
												class={cx(style.userLink, style[theme], style[primaryLinkMode], isEmailLink && isEmailPreviewEnabled && style.emailLink)}
												link={link}
												onMouseEnter={isEmailLink && isEmailPreviewEnabled ? handleEmailMouseEnter : null}
												onMouseLeave={isEmailLink && isEmailPreviewEnabled ? handleEmailMouseLeave : null}
											>
												{linkEventImage(link, { theme, class: style.linkRendererImage })}
												{linkEventIcon(link, { theme, class: style.linkRendererIcon })}
												{linkEventLabel(link, { theme, class: style.linkRendererLabel })}
											</LinkEvent>
											{showEmailPreview && (
												<EmailPreview config={emailPreview} />
											)}
										</div>
									);
								})
							}
						</div>
					}
				</TrackingProvider>
			</WidthConstrain>
		</Row>
	);
}

function Navigation({ theme, navLinks, centeredNavigation, clientLinks, nonClientLinks, class: cls, current, navType, onMenuOpen, showWideView }) {
	return (
		<TrackingProvider {...{
			[DIMENSION.componentpath]: 'Navigation'
		}}
		>
			<nav class={cx('row', style.navigation, centeredNavigation && style.centeredNavigation, style[theme], cls)}>
				<WidthConstrain class={style.widthConstrain} showWideView={showWideView}>
					<TrackingProvider {...{
						[DIMENSION.componentpath]: 'NavigationLinks'
					}}
					>
						<TrackNav
							class={cx(style.navLinks)}
							skin={navSkinRounded(theme)}
							type={navType && (navType.navigation || navType.default || navType) || 'hover'}
							delaySubnavRender
							items={navLinks}
							current={current}
							onMenuOpen={onMenuOpen}
						/>
					</TrackingProvider>
					{
						nonClientLinks && nonClientLinks.length > 0 &&
						<TrackingProvider {...{
							[DIMENSION.componentpath]: 'NonClientLinks'
						}}
						>
							<TrackNav
								class={cx(style.nonClientLinks)}
								skin={navSkinRounded(theme)}
								type={navType && (navType.navigation || navType.default || navType) || 'hover'}
								delaySubnavRender
								items={nonClientLinks}
								openLeft
								current={current}
								onMenuOpen={onMenuOpen}
							/>
						</TrackingProvider>
					}
					<TrackingProvider {...{
						[DIMENSION.componentpath]: 'ClientLinks'
					}}
					>
						<TrackNav
							class={cx(style.clientLinks)}
							skin={navSkinRounded(theme)}
							type={navType && (navType.navigation || navType.default || navType) || 'hover'}
							delaySubnavRender
							items={clientLinks}
							openLeft
							current={current}
							onMenuOpen={onMenuOpen}
						/>
					</TrackingProvider>
				</WidthConstrain>
			</nav>
		</TrackingProvider>
	);
}

@trackingProvider({
	[DIMENSION.componentpath]: 'DesktopHeader'
})
@configFromCurrent({ device: 'desktop' })
export class DesktopHeader extends Component {

	state = {
		navMenuIsOpen: false,
		specialLinks: []
	}

	handleMenuOpen = (isOpen, link, meta, isSubMenu, incomingContext) => {
		if (!isSubMenu && this.state.navMenuIsOpen !== isOpen) {
			this.setState({ navMenuIsOpen: isOpen });

			/*
			resetScrollDirectionVertical is provided by ScrollDirection.
				We are calling it so that the nav does not hide as soon
				as a submenu closes if the user scrolled while it was open.
			*/
			if (!isOpen && this.props.resetScrollDirectionVertical) this.props.resetScrollDirectionVertical();
		}
	}

	/*
	This Oberserver is used to check for changes to the document DOM.
	When changes happen we are going to see if our offset changed
	and ensure that our fixedToTop setting is still accurate.
	*/

	startOffsetObserver = () => {
		this.observer = new MutationObserver(this.checkForOffsetChange);
		this.observer.observe(document, { childList: true, subtree: true });
	}

	stopOffsetObserver = () => {
		if (this.observer) this.observer.disconnect();
	}

	checkForOffsetChange = () => {
		if (!this.placeholderRef) return;
		let top = this.placeholderRef.offsetTop;
		if (top !== this.lastTop) {
			this.handleOffsetForFixedToTop();
		}
		this.lastTop = top;
	}

	handleDirectionChange = e => {
		this.setState(e);
	}

	handleOffsetForFixedToTop = pos => {
		if (pos === undefined) pos = this.lastPos;
		this.offsetCheckRan = true;
		if (this.placeholderRef && this.props.fixedToTop) {
			let top = this.placeholderRef.offsetTop;
			let dif = top - pos;
			if (dif > 0 && !this.state.blockFixedTop) {
				this.setState({ blockFixedTop: true });
			}
			else if (dif <= 0 && this.state.blockFixedTop) {
				this.setState({ blockFixedTop: false });
			}
			this.setState({ scrollPosition: pos });
			this.lastPos = pos;
			this.lastTop = top;
		}
	}

	capturePlaceholderRef = ref => {
		this.placeholderRef = ref;
	}

	static defaultProps = {
		theme: 'light',
		scrollDirectionVertical: 0,
		initialScrollDirection: true,
		displayTopHat: true,
		displayNavigation: true,
		displayPrimary: true,
		allowHideNavigation: true,
		fixedToTop: true,
		placeholdHeight: true,
		forceFocus: false
	}

	componentWillMount () {
		this.setState({
			searchColorTheme: `colorScheme${Math.floor(Math.random() * 3) + 1}`,
			navLinks: createNavigation(this.props.navigationSections, this.props.navigationLinks)
		});

		if (this.props.showSpecial) {
			fetchSpecialTitle(this.props.streamClient, this.props.startContentServiceOrigin).then(( results ) => {
				let title = dlv(results, 'cards.0.title');
				if (title) {
					let item = {
						url: '/special/title/' + title,
						currentWhen: '^/special'
					};

					let images = dlv(results, 'cards.0.assets.0.images');

					if (images && images.length === 2) {
						item.image = {
							light: images[0].src,
							dark: images[1].src
						};
					}
					else {
						item.label = title;
					}
					this.setState({
						specialLinks: [item]
					});
				}
			});
		}
	}

	componentDidMount () {
		this.startOffsetObserver();
	}

	componentWillUnmount() {
		this.stopOffsetObserver();
	}

	render({
		theme, navigationTheme, fixedToTop, placeholdHeight, tophatTheme,
		displayPrimary, displayTopHat, displayNavigation,
		allowHideNavigation, customHeader, hideLogo, emailPreview,
		...props
	},
	{
		navMenuIsOpen, scrollDirectionVertical, blockFixedTop, specialLinks, scrollPosition, searchColorTheme, navLinks
	}
	) {
		fixedToTop = (blockFixedTop || !this.offsetCheckRan) ? false : fixedToTop;
		allowHideNavigation = blockFixedTop ? false : allowHideNavigation;
		let hasScrolledVertically = scrollPosition > 0,
			hidingNavigation = !displayNavigation || (!navMenuIsOpen && allowHideNavigation && scrollDirectionVertical > 0);

		let header = (
			<header class={cx(style.header, fixedToTop && style.fixedToTop, style[theme])}>
				<ScrollDirection
					debounce={1}
					onDirectionChange={this.handleDirectionChange}
					onScroll={this.handleOffsetForFixedToTop}
				/>

				{ customHeader && customHeader.iframeParameters &&
					<div style={hasScrolledVertically && 'display:none;'}>
						<iframe {...customHeader.iframeParameters}>
							<p>Your browser does not support iframes.</p>
						</iframe>
					</div>
				}

				{ displayTopHat &&
					(
						<TopHat
							theme={tophatTheme || theme}
							onMenuOpen={this.handleMenuOpen}
							{...props}
						/>
					)
				}

				{displayPrimary && (
					<PrimaryHeader
						theme={theme}
						hideLogo={hideLogo && !hasScrolledVertically}
						window={window}
						class={hidingNavigation ? style.hideNav : style.showNav}
						searchColorTheme={searchColorTheme}
						emailPreview={emailPreview}
						{...props}
					/>
				)}

				{displayNavigation && (
					<Navigation
						theme={navigationTheme || theme}
						navLinks={navLinks && navLinks.concat(specialLinks)}
						class={cx(fixedToTop && style.fixedToTop, hidingNavigation ? style.hideNav : style.showNav)}
						onMenuOpen={this.handleMenuOpen}
						{...props}
					/>
				)}
			</header>
		);

		if (placeholdHeight) {
			header = (
				<div
					ref={this.capturePlaceholderRef}
					class={cx(
						style.headerPlaceholder,
						fixedToTop && style.fixedToTop,
						hidingNavigation ? style.hideNav : style.showNav,
						hasScrolledVertically && style.scrolled
					)}
				>
					{ header }
				</div>
			);
		}

		return (
			<Provider theme={theme}>{header}</Provider>
		);
	}
}

const Header = withConfig([
	'startContentServiceOrigin',
	'locationServiceOrigin',
	'weatherServiceOrigin',
	'portalStaticOrigin',
	'streamClient',

	'headerWidget.theme',
	'headerWidget.navigationTheme',

	'headerWidget.desktop.logo',
	'headerWidget.logo',

	'headerWidget.desktop.weatherPlacement',
	'headerWidget.desktop.useLargeLogo',
	'headerWidget.desktop.hideLogo',

	'headerWidget.desktop.displayPrimary',
	'headerWidget.desktop.displayTopHat',
	'headerWidget.desktop.displayNavigation',
	'headerWidget.desktop.customHeader',

	'headerWidget.navigationSections',
	'headerWidget.desktop.navigationLinks',
	'headerWidget.navigationLinks',
	'headerWidget.desktop.centeredNavigation',

	'headerWidget.desktop.showSpecial',
	'headerWidget.showSpecial',

	'headerWidget.desktop.clientLinks',
	'headerWidget.clientLinks',

	'headerWidget.desktop.nonClientLinks',
	'headerWidget.nonClientLinks',

	'headerWidget.desktop.leadingAltLinks',
	'headerWidget.leadingAltLinks',

	'headerWidget.desktop.leadingLinks',
	'headerWidget.leadingLinks',

	'headerWidget.primaryLinks',
	'headerWidget.desktop.primaryLinks',
	'headerWidget.desktop.primaryLinkMode',

	'headerWidget.desktop.navType',

	'headerWidget.desktop.tophatTheme',

	'headerWidget.desktop.fixedToTop',
	'headerWidget.fixedToTop',

	'headerWidget.desktop.placeholdHeight',
	'headerWidget.placeholdHeight',

	'headerWidget.desktop.search.forceFocus',
	'headerWidget.search.forceFocus',

	'headerWidget.emailPreview'
])(
	({ layout, ...props }) => <DesktopHeader data-type="component" {...props} />
);

export default Header;
